import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  saGetOrganizations,
  superAdminDashboard,
  superAdminGetTransactions,
  superAdminGetUsers,
  superAdminGetProjects,
  superAdminRevenueAnalytics,
  superAdminUserGrowthAnalytics,
  superAdminSuspendUser,
} from '../../api/api';
import StyledSelect from '../../components/StyledSelect';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from 'recharts';
import {
  Building2,
  FolderKanban,
  Receipt,
  Shield,
  TrendingDown,
  TrendingUp,
  Users,
  Eye,
  Pencil,
  ShieldBan,
  ShieldCheck,
  Search,
  X,
} from 'lucide-react';
import {
  ActionIconButton,
  formatDateTime,
  GlobalRoleChip,
  OrgRoleChip,
  StatusChip,
} from '../components/SuperAdminUi';

const primaryCards = [
  { key: 'totalUsers', label: 'Total Users', icon: Users, color: '#f472b6' },
  { key: 'totalAdmins', label: 'Total Admins', icon: Shield, color: '#fbbf24' },
  { key: 'totalProjects', label: 'Total Projects', icon: FolderKanban, color: '#60a5fa' },
  { key: 'totalTransactions', label: 'Total Transactions', icon: Receipt, color: '#34d399' },
  { key: 'totalIncome', label: 'Total Income', icon: TrendingUp, color: '#4ade80' },
  { key: 'totalExpenses', label: 'Total Expenses', icon: TrendingDown, color: '#fb7185' },
];

const organizationCards = [
  { key: 'total_organizations', label: 'Total Organizations', icon: Building2, color: '#7c3aed' },
  { key: 'active_organizations', label: 'Active Organizations', icon: Building2, color: '#16a34a' },
  { key: 'suspended_organizations', label: 'Suspended Organizations', icon: Building2, color: '#dc2626' },
];

const emptyDetailFilters = {
  organizationId: '',
  globalRole: '',
  orgRole: '',
  query: '',
  projectId: '',
};

const detailState = {
  open: false,
  title: '',
  kind: '',
  metricKey: '',
  rows: [],
  projects: [],
};

const normalize = (value) => String(value ?? '').trim().toLowerCase();

function CenteredModal({ open, title, subtitle, onClose, children, wide = false }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-3 py-4 backdrop-blur-sm">
     <div
  className={`w-full max-h-[85vh] overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-slate-200 ${
    wide ? 'max-w-6xl' : 'max-w-3xl'
  }`}
>
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 sm:px-5">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold tracking-tight text-slate-900 sm:text-lg">
              {title}
            </h3>
            {subtitle ? (
              <p className="mt-0.5 truncate text-[11px] text-slate-500 sm:text-xs">{subtitle}</p>
            ) : null}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
            aria-label="Close modal"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-4 py-4 sm:px-5 sm:py-5">{children}</div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, onClick, currency, isMoney = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="superadmin-kpi group w-full rounded-2xl border border-slate-100 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md sm:p-5"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 sm:text-[11px]">
            {label}
          </p>
          <p className="mt-1.5 truncate text-xl font-extrabold tracking-tight text-slate-900 sm:text-2xl">
            {isMoney ? `${currency} ${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : value ?? 0}
          </p>
        </div>

        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl"
          style={{ background: `${color}18`, color }}
        >
          <Icon size={18} />
        </div>
      </div>
    </button>
  );
}

function FieldChip({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</p>
      <p className="mt-1 break-words text-xs font-medium text-slate-700">{value ?? '-'}</p>
    </div>
  );
}

function RowActionBar({ kind, row, onView, onManage, onSuspendToggle }) {
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      <ActionIconButton icon={Eye} label="View" tone="view" onClick={() => onView(row)} />
      <ActionIconButton icon={Pencil} label="Manage" tone="edit" onClick={() => onManage(kind, row)} />
      {kind === 'users' ? (
        <ActionIconButton
          icon={Number(row.is_blocked) ? ShieldCheck : ShieldBan}
          label={Number(row.is_blocked) ? 'Activate' : 'Suspend'}
          tone={Number(row.is_blocked) ? 'success' : 'edit'}
          onClick={() => onSuspendToggle(row, !Number(row.is_blocked))}
        />
      ) : null}
    </div>
  );
}

export default function SuperAdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [dashboard, setDashboard] = useState(null);
  const [revenue, setRevenue] = useState([]);
  const [userGrowth, setUserGrowth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [detail, setDetail] = useState({
    ...detailState,
  });

  const [detailFilters, setDetailFilters] = useState(emptyDetailFilters);
  const [selectedRow, setSelectedRow] = useState(null);

  const formatMoney = (amount, currency = 'PKR') =>
    `${currency} ${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  const primaryCurrency = useMemo(() => {
    const totals = dashboard?.currencyTotals || [];
    if (!Array.isArray(totals) || totals.length === 0) return 'PKR';
    return totals.length === 1 ? (totals[0].currency || 'PKR') : 'MIXED';
  }, [dashboard]);

  const ratioData = useMemo(() => {
    const income = Number(dashboard?.totalIncome || 0);
    const expense = Number(dashboard?.totalExpenses || 0);
    return [
      { name: 'Income', value: income },
      { name: 'Expenses', value: expense },
    ];
  }, [dashboard]);

  const resetDetail = () => {
    setDetail({ ...detailState });
    setDetailFilters(emptyDetailFilters);
  };

  const loadDashboard = async () => {
    setLoading(true);
    setError('');

    try {
      const [dashRes, revRes, growthRes] = await Promise.all([
        superAdminDashboard(),
        superAdminRevenueAnalytics(),
        superAdminUserGrowthAnalytics(),
      ]);

      setDashboard(dashRes?.data?.data || {});
      setRevenue(revRes?.data?.data || []);
      setUserGrowth(growthRes?.data?.data || []);
    } catch (requestError) {
      setDashboard(null);
      setRevenue([]);
      setUserGrowth([]);
      setError(requestError?.response?.data?.message || 'Failed to load command center metrics.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const openUsersDetail = async (kind = 'users') => {
    try {
      // For admin drill-down: fetch both platform admins AND org-level admins
      let users = [];
      if (kind === 'totalAdmins') {
        const [platformRes, orgRes] = await Promise.all([
          superAdminGetUsers({ page: 1, limit: 500, role: 'admin' }),
          superAdminGetUsers({ page: 1, limit: 500, org_role: 'admin' }),
        ]);
        const platformAdmins = platformRes?.data?.data?.users || [];
        const orgAdmins = orgRes?.data?.data?.users || [];
        // Merge deduped by user id
        const seen = new Set();
        users = [...platformAdmins, ...orgAdmins].filter((row) => {
          if (seen.has(row.id)) return false;
          seen.add(row.id);
          return true;
        });
      } else {
        const response = await superAdminGetUsers({ page: 1, limit: 500 });
        users = response?.data?.data?.users || [];
      }

      users = users.filter((row) => {
        if (normalize(row?.global_role) === 'super_admin') return false;
        if (Number(row?.id) === Number(user?.id)) return false;
        return true;
      });

      setDetail({
        open: true,
        title: kind === 'totalAdmins' ? 'Admin Accounts' : 'All Organization Users',
        kind: 'users',
        metricKey: kind,
        rows: users,
        projects: [],
      });

      setDetailFilters(emptyDetailFilters);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Failed to load users.');
    }
  };

  const openProjectsDetail = async () => {
    try {
      const response = await superAdminGetProjects({ page: 1, limit: 500 });
      setDetail({
        open: true,
        title: 'Projects',
        kind: 'projects',
        metricKey: 'totalProjects',
        rows: response?.data?.data?.projects || [],
        projects: [],
      });
      setDetailFilters(emptyDetailFilters);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Failed to load projects.');
    }
  };

  const openOrganizationsDetail = async (status) => {
    try {
      const params = { page: 1, limit: 500 };
      if (status) params.status = status;

      const response = await saGetOrganizations(params);
      setDetail({
        open: true,
        title: status ? `${status[0].toUpperCase()}${status.slice(1)} Organizations` : 'All Organizations',
        kind: 'organizations',
        metricKey: status ? `${status}_organizations` : 'total_organizations',
        rows: response?.data?.data?.organizations || [],
        projects: [],
      });
      setDetailFilters(emptyDetailFilters);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Failed to load organizations.');
    }
  };

  const loadTransactionDetail = async (projectId = '') => {
    try {
      const [txRes, projectRes] = await Promise.all([
        superAdminGetTransactions({
          page: 1,
          limit: 1000,
          ...(projectId ? { project_id: projectId } : {}),
        }),
        superAdminGetProjects({ page: 1, limit: 1000 }),
      ]);

      const projects = (projectRes?.data?.data?.projects || []).map((p) => ({
        value: String(p.id),
        label: `${p.project_name || p.name || 'Unnamed Project'}${p.organization_name ? ` • ${p.organization_name}` : ''}`,
      }));

      setDetail({
        open: true,
        title: projectId ? 'Project Transactions' : 'All Transactions',
        kind: 'transactions',
        metricKey: projectId ? 'projectTransactions' : 'totalTransactions',
        rows: txRes?.data?.data?.transactions || [],
        projects,
      });

      setDetailFilters((current) => ({ ...current, projectId: String(projectId || '') }));
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Failed to load transaction details.');
    }
  };

  const openDetail = async (key) => {
    if (key === 'totalUsers') return openUsersDetail('users');
    if (key === 'totalAdmins') return openUsersDetail('totalAdmins');
    if (key === 'totalProjects') return openProjectsDetail();
    if (key === 'total_organizations') return openOrganizationsDetail('');
    if (key === 'active_organizations') return openOrganizationsDetail('active');
    if (key === 'suspended_organizations') return openOrganizationsDetail('suspended');

    if (key === 'totalTransactions') {
      return loadTransactionDetail('');
    }

    if (key === 'totalIncome') {
      try {
        const [txRes, projectRes] = await Promise.all([
          superAdminGetTransactions({ page: 1, limit: 1000, type: 'receipt' }),
          superAdminGetProjects({ page: 1, limit: 1000 }),
        ]);
        setDetail({
          open: true,
          title: 'Revenue Transactions',
          kind: 'transactions',
          metricKey: 'totalIncome',
          rows: txRes?.data?.data?.transactions || [],
          projects: (projectRes?.data?.data?.projects || []).map((p) => ({
            value: String(p.id),
            label: `${p.project_name || p.name || 'Unnamed Project'}${p.organization_name ? ` • ${p.organization_name}` : ''}`,
          })),
        });
        setDetailFilters(emptyDetailFilters);
      } catch (requestError) {
        setError(requestError?.response?.data?.message || 'Failed to load revenue transactions.');
      }
      return;
    }

    if (key === 'totalExpenses') {
      try {
        const [txRes, projectRes] = await Promise.all([
          superAdminGetTransactions({ page: 1, limit: 1000, type: 'payment' }),
          superAdminGetProjects({ page: 1, limit: 1000 }),
        ]);
        setDetail({
          open: true,
          title: 'Expense Transactions',
          kind: 'transactions',
          metricKey: 'totalExpenses',
          rows: txRes?.data?.data?.transactions || [],
          projects: (projectRes?.data?.data?.projects || []).map((p) => ({
            value: String(p.id),
            label: `${p.project_name || p.name || 'Unnamed Project'}${p.organization_name ? ` • ${p.organization_name}` : ''}`,
          })),
        });
        setDetailFilters(emptyDetailFilters);
      } catch (requestError) {
        setError(requestError?.response?.data?.message || 'Failed to load expense transactions.');
      }
    }
  };

  const handleDashboardSuspend = async (row, shouldBlock) => {
    try {
      await superAdminSuspendUser(row.id, { is_blocked: shouldBlock });
      setDetail((current) => ({
        ...current,
        rows: current.rows.map((item) =>
          Number(item.id) === Number(row.id)
            ? { ...item, is_blocked: shouldBlock ? 1 : 0 }
            : item
        ),
      }));

      if (selectedRow && Number(selectedRow.id) === Number(row.id)) {
        setSelectedRow((current) => ({ ...current, is_blocked: shouldBlock ? 1 : 0 }));
      }
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Failed to update user status.');
    }
  };

  const handleManageRow = (kind, row, metricKey = '') => {
    if (kind === 'users') {
      const params = new URLSearchParams();
      if (row?.id) params.set('userId', String(row.id));
      if (metricKey === 'totalAdmins') params.set('scope', 'admins');
      return navigate(`/super-admin/users${params.toString() ? `?${params.toString()}` : ''}`);
    }
    if (kind === 'organizations') {
      const params = new URLSearchParams();
      if (row?.id) params.set('orgId', String(row.id));
      return navigate(`/super-admin/organizations${params.toString() ? `?${params.toString()}` : ''}`);
    }
    if (kind === 'projects') {
      const params = new URLSearchParams();
      if (row?.id) params.set('projectId', String(row.id));
      return navigate(`/super-admin/projects${params.toString() ? `?${params.toString()}` : ''}`);
    }
    if (kind === 'transactions') {
      const params = new URLSearchParams();
      if (row?.id) params.set('transactionId', String(row.id));
      if (metricKey === 'totalIncome') params.set('type', 'receipt');
      if (metricKey === 'totalExpenses') params.set('type', 'payment');
      return navigate(`/super-admin/transactions${params.toString() ? `?${params.toString()}` : ''}`);
    }
    navigate('/super-admin/reports');
  };

  if (loading) {
    return <div className="text-slate-500">Loading command center metrics...</div>;
  }

  if (!dashboard) {
    return <div className="text-slate-500">{error || 'Failed to load command center metrics.'}</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="superadmin-title text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
          Enterprise Command Center
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          System-wide intelligence across users, tenants, revenue, and privileged activity.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => navigate('/register')}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            Open Shared Registration Flow
          </button>
          <button
            type="button"
            onClick={() => navigate('/join')}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            Open Invitation Join Flow
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {primaryCards.map(({ key, label, icon: Icon, color }) => (
          <StatCard
            key={key}
            label={label}
            value={dashboard[key]}
            icon={Icon}
            color={color}
            onClick={() => openDetail(key)}
            currency={primaryCurrency}
            isMoney={key.includes('Income') || key.includes('Expenses')}
          />
        ))}
      </div>

      <section className="superadmin-glass rounded-3xl p-4 lg:p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-800">Organization Overview</p>
            <p className="text-xs text-slate-500">
              Click a tenant metric to drill into the responsive control view.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {organizationCards.map(({ key, label, icon: Icon, color }) => (
            <button
              key={key}
              type="button"
              onClick={() => openDetail(key)}
              className="rounded-2xl border border-slate-100 bg-white p-4 text-left transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.14em] text-slate-400">{label}</p>
                  <p className="mt-2 text-2xl font-extrabold" style={{ color }}>
                    {dashboard?.org_stats?.[key] ?? 0}
                  </p>
                </div>
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-xl"
                  style={{ background: `${color}1a`, color }}
                >
                  <Icon size={18} />
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="superadmin-glass rounded-3xl p-4 xl:col-span-2">
          <p className="mb-4 text-sm font-semibold text-slate-700">Monthly Financial Trend</p>
          <div style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenue}>
                <defs>
                  <linearGradient id="inc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4ade80" stopOpacity={0.6} />
                    <stop offset="95%" stopColor="#4ade80" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="exp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#fb7185" stopOpacity={0.55} />
                    <stop offset="95%" stopColor="#fb7185" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" stroke="#64748b" />
                <YAxis stroke="#64748b" />
                <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #e2e8f0' }} />
                <Area type="monotone" dataKey="revenue" stroke="#4ade80" fill="url(#inc)" />
                <Area type="monotone" dataKey="expenses" stroke="#fb7185" fill="url(#exp)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="superadmin-glass rounded-3xl p-4">
          <p className="mb-4 text-sm font-semibold text-slate-700">Expense vs Income</p>
          <div style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={ratioData} dataKey="value" innerRadius={65} outerRadius={102} paddingAngle={4}>
                  <Cell fill="#4ade80" />
                  <Cell fill="#fb7185" />
                </Pie>
                <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #e2e8f0' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.3fr_1fr]">
        <div className="superadmin-glass rounded-3xl p-4">
          <p className="mb-4 text-sm font-semibold text-slate-700">User Growth & Admin Velocity</p>
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={userGrowth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" stroke="#64748b" />
                <YAxis stroke="#64748b" />
                <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #e2e8f0' }} />
                <Legend />
                <Line type="monotone" dataKey="users" stroke="#c44569" strokeWidth={3} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="admins" stroke="#f59e0b" strokeWidth={3} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="superadmin-glass rounded-3xl p-4">
          <p className="mb-3 text-sm font-semibold text-slate-700">Recent Platform Activities</p>
          <div className="space-y-3">
            {(dashboard.recentActivities || []).slice(0, 6).map((activity) => (
              <button
                key={activity.id}
                type="button"
                onClick={() => setSelectedRow(activity)}
                className="w-full rounded-2xl border border-slate-100 bg-white p-3 text-left transition hover:bg-slate-50"
              >
                <p className="text-sm font-semibold text-slate-900">{activity.action}</p>
                <p className="mt-1 text-xs text-slate-500">
                  {activity.entity_type || 'entity'} #{activity.entity_id || '-'} ·{' '}
                  {activity.actor_name || activity.actor_email || 'system'}
                </p>
                <p className="mt-2 text-xs text-slate-400">{formatDateTime(activity.created_at)}</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      <CenteredModal
        open={detail.open}
        title={detail.title}
        subtitle={
          detail.kind === 'transactions'
            ? 'Select a project to view all related transactions.'
            : 'Drill-down data from the platform command center.'
        }
        onClose={resetDetail}
        wide
      >
        <DrilldownContent
          detail={detail}
          filters={detailFilters}
          onFiltersChange={setDetailFilters}
          setSelectedRow={setSelectedRow}
          onManage={handleManageRow}
          onSuspendToggle={handleDashboardSuspend}
          formatMoney={formatMoney}
          onProjectChange={loadTransactionDetail}
        />
      </CenteredModal>

      <CenteredModal
        open={!!selectedRow}
        title={selectedRow ? getSelectedTitle(selectedRow) : ''}
        subtitle={selectedRow ? getSelectedSubtitle(selectedRow) : ''}
        onClose={() => setSelectedRow(null)}
        wide
      >
        {selectedRow ? <SelectedRowDetail row={selectedRow} formatMoney={formatMoney} /> : null}
      </CenteredModal>
    </div>
  );
}

function DrilldownContent({
  detail,
  filters,
  onFiltersChange,
  setSelectedRow,
  onManage,
  onSuspendToggle,
  formatMoney,
  onProjectChange,
}) {
  const updateFilters = (patch) => onFiltersChange((current) => ({ ...current, ...patch }));

  const orgOptions = React.useMemo(() => {
    const map = new Map();
    detail.rows.forEach((row) => {
      const orgId = detail.kind === 'organizations' ? row.id : row.organization_id;
      const orgName = detail.kind === 'organizations' ? row.name : row.organization_name;
      if (orgId && orgName && !map.has(String(orgId))) {
        map.set(String(orgId), { value: String(orgId), label: orgName });
      }
    });
    return [{ value: '', label: 'All Organizations' }, ...Array.from(map.values())];
  }, [detail.kind, detail.rows]);

  const projectOptions = React.useMemo(() => {
    if (detail.kind !== 'transactions') return [];
    return [{ value: '', label: 'All Projects' }, ...(detail.projects || [])];
  }, [detail.kind, detail.projects]);

  const globalRoleOptions = React.useMemo(() => {
    if (detail.kind !== 'users') return [];
    const values = new Set(detail.rows.map((row) => String(row.global_role || '')).filter(Boolean));
    const items = Array.from(values).sort();
    return [{ value: '', label: 'All Global Roles' }, ...items.map((value) => ({ value, label: value.replace(/_/g, ' ') }))];
  }, [detail.kind, detail.rows]);

  const orgRoleOptions = React.useMemo(() => {
    if (detail.kind !== 'users') return [];
    const values = new Set(detail.rows.map((row) => String(row.org_role || '')).filter(Boolean));
    const items = Array.from(values).sort();
    return [{ value: '', label: 'All Organization Roles' }, ...items.map((value) => ({ value, label: value.replace(/_/g, ' ') }))];
  }, [detail.kind, detail.rows]);

  const rows = React.useMemo(() => {
    const query = String(filters.query || '').trim().toLowerCase();

    return detail.rows.filter((row) => {
      const orgId = detail.kind === 'organizations' ? row.id : row.organization_id;
      if (filters.organizationId && String(orgId || '') !== String(filters.organizationId)) return false;

      if (detail.kind === 'users') {
        if (filters.globalRole && String(row.global_role || '') !== String(filters.globalRole)) return false;
        if (filters.orgRole && String(row.org_role || '') !== String(filters.orgRole)) return false;
      }

      if (detail.kind === 'transactions' && filters.projectId) {
        if (String(row.project_id || '') !== String(filters.projectId)) return false;
      }

      if (!query) return true;

      const haystack = [
        row.name,
        row.email,
        row.organization_name,
        row.org_role,
        row.global_role,
        row.project_name,
        row.user_name,
        row.type,
        row.status,
        row.code,
        row.description,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [detail.rows, detail.kind, filters]);

  const handleProjectSelect = async (value) => {
    updateFilters({ projectId: value });
    if (typeof onProjectChange === 'function') {
      await onProjectChange(value);
    }
  };

  if (!detail.rows.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-5 py-8 text-center text-sm text-slate-500">
        No detail rows available for this metric.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">
        <label className="block">
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.11em] text-slate-400">
            Search
          </span>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
            <input
              value={filters.query}
              onChange={(event) => updateFilters({ query: event.target.value })}
              placeholder="Search rows"
              className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-8 pr-3 text-xs text-slate-700 outline-none focus:border-rose-300 focus:ring-2 focus:ring-rose-100"
            />
          </div>
        </label>

        <label className="block">
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.11em] text-slate-400">
            Organization
          </span>
          <StyledSelect
            value={filters.organizationId}
            onChange={(value) => updateFilters({ organizationId: value })}
            options={orgOptions}
            searchable
            placeholder="All Orgs"
          />
        </label>

        {detail.kind === 'transactions' ? (
          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.11em] text-slate-400">
              Project
            </span>
            <StyledSelect
              value={filters.projectId}
              onChange={handleProjectSelect}
              options={projectOptions}
              searchable
              placeholder="All Projects"
            />
          </label>
        ) : detail.kind === 'users' ? (
          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.11em] text-slate-400">
              Global Role
            </span>
            <StyledSelect
              value={filters.globalRole}
              onChange={(value) => updateFilters({ globalRole: value })}
              options={globalRoleOptions}
              searchable
              placeholder="All Roles"
            />
          </label>
        ) : (
          <div className="hidden xl:block" />
        )}

        {detail.kind === 'users' ? (
          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.11em] text-slate-400">
              Org Role
            </span>
            <StyledSelect
              value={filters.orgRole}
              onChange={(value) => updateFilters({ orgRole: value })}
              options={orgRoleOptions}
              searchable
              placeholder="All Org Roles"
            />
          </label>
        ) : (
          <div className="hidden xl:block" />
        )}
      </div>

      {(filters.query || filters.organizationId || filters.globalRole || filters.orgRole || filters.projectId) ? (
        <button
          type="button"
          onClick={() => {
            onFiltersChange(emptyDetailFilters);
            if (detail.kind === 'transactions' && typeof onProjectChange === 'function') {
              onProjectChange('');
            }
          }}
          className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600 transition hover:bg-slate-100"
        >
          Clear
        </button>
      ) : null}

      {rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-5 py-7 text-center text-sm text-slate-400">
          No rows matched the current filters.
        </div>
      ) : null}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
  <div className="max-h-[58vh] overflow-auto">
    <table className="w-full min-w-[1000px] border-collapse">
      <thead className="sticky top-0 z-10 bg-slate-100">
        <tr>
          {getColumns(detail.kind).map((column) => (
            <th
              key={column.label}
              className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500"
            >
              {column.label}
            </th>
          ))}

          <th className="border-b border-slate-200 px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
            Actions
          </th>
        </tr>
      </thead>

      <tbody>
        {rows.map((row, index) => (
          <tr
            key={row.id || row.membership_id || index}
            className="transition hover:bg-slate-50"
          >
            {getColumns(detail.kind).map((column) => (
              <td
                key={`${column.label}-${index}`}
                className="border-b border-slate-100 px-4 py-3 text-xs text-slate-700"
              >
                {column.render
                  ? column.render(row, formatMoney)
                  : row[column.key] || '-'}
              </td>
            ))}

            <td className="border-b border-slate-100 px-4 py-3">
              <div className="flex items-center justify-end gap-2">
                <ActionIconButton
                  icon={Eye}
                  label="View"
                  tone="view"
                  onClick={() => setSelectedRow(row)}
                />

                <ActionIconButton
                  icon={Pencil}
                  label="Manage"
                  tone="edit"
                  onClick={() => onManage(detail.kind, row, detail.metricKey)}
                />

                {detail.kind === 'users' ? (
                  <ActionIconButton
                    icon={Number(row.is_blocked) ? ShieldCheck : ShieldBan}
                    label={Number(row.is_blocked) ? 'Activate' : 'Suspend'}
                    tone={Number(row.is_blocked) ? 'success' : 'edit'}
                    onClick={() =>
                      onSuspendToggle(row, !Number(row.is_blocked))
                    }
                  />
                ) : null}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
</div>
    </div>
  );
}

function getMiniFields(kind, row, formatMoney) {
  if (kind === 'users') {
    return [
      { label: 'Org Role', value: row.org_role || '-' },
      { label: 'Global Role', value: row.global_role || '-' },
      { label: 'Status', value: Number(row.is_blocked) ? 'Suspended' : 'Active' },
      { label: 'Last Login', value: formatDateTime(row.last_login_at) || '-' },
    ];
  }

  if (kind === 'organizations') {
    return [
      { label: 'Code', value: row.code || row.organization_code || '-' },
      { label: 'Plan', value: row.subscription_plan || '-' },
      { label: 'Status', value: row.status || '-' },
      { label: 'Members', value: row.member_count ?? '-' },
    ];
  }

  if (kind === 'projects') {
    return [
      { label: 'Owner', value: row.owner_name || '-' },
      { label: 'Status', value: row.status || '-' },
      { label: 'Budget', value: formatMoney(row.budget || 0, row.currency || 'PKR') },
      { label: 'Currency', value: row.currency || 'PKR' },
    ];
  }

  return [
    { label: 'Date', value: row.date || row.transaction_date || '-' },
    { label: 'Project', value: row.project_name || '-' },
    { label: 'Type', value: row.type || '-' },
    { label: 'Amount', value: formatMoney(row.amount || 0, row.currency || 'PKR') },
  ];
}

function getColumns(kind) {
  if (kind === 'users') {
    return [
      { key: 'name', label: 'User' },
      { key: 'email', label: 'Email' },
      { key: 'organization_name', label: 'Organization' },
      { key: 'org_role', label: 'Organization Role', render: (row) => <OrgRoleChip role={row.org_role} /> },
      { key: 'global_role', label: 'Global Role', render: (row) => <GlobalRoleChip role={row.global_role} /> },
      { key: 'is_blocked', label: 'Status', render: (row) => <StatusChip status={Number(row.is_blocked) ? 'suspended' : 'active'} /> },
      { key: 'last_login_at', label: 'Last Login', render: (row) => formatDateTime(row.last_login_at) },
    ];
  }

  if (kind === 'organizations') {
    return [
      { key: 'name', label: 'Organization' },
      { key: 'code', label: 'Code' },
      { key: 'subscription_plan', label: 'Plan' },
      { key: 'status', label: 'Status', render: (row) => <StatusChip status={row.status} /> },
      { key: 'member_count', label: 'Members' },
    ];
  }

  if (kind === 'projects') {
    return [
      { key: 'name', label: 'Project' },
      { key: 'owner_name', label: 'Owner' },
      { key: 'status', label: 'Status', render: (row) => <StatusChip status={row.status || 'active'} /> },
      { key: 'budget', label: 'Budget', render: (row, money) => money(row.budget || 0, row.currency || 'PKR') },
    ];
  }

  return [
    { key: 'date', label: 'Date' },
    { key: 'user_name', label: 'User' },
    { key: 'project_name', label: 'Project' },
    { key: 'type', label: 'Type', render: (row) => <StatusChip status={row.type} /> },
    { key: 'amount', label: 'Amount', render: (row, money) => money(row.amount || 0, row.currency || 'PKR') },
  ];
}

function getPrimaryLabel(kind, row) {
  if (kind === 'organizations') return row.name;
  if (kind === 'projects') return row.name || row.project_name;
  if (kind === 'transactions') return row.description || row.project_name || 'Transaction';
  return row.name;
}

function getSecondaryLabel(kind, row, formatMoney) {
  if (kind === 'organizations') return `${row.subscription_plan || 'free'} plan · ${row.status || 'active'}`;
  if (kind === 'projects') return `${row.owner_name || 'Unknown owner'} · ${formatMoney(row.budget || 0, row.currency || 'PKR')}`;
  if (kind === 'transactions') return `${row.user_name || 'Unknown'} · ${formatMoney(row.amount || 0, row.currency || 'PKR')}`;
  return `${row.organization_name || 'No organization'} · ${row.email || ''}`;
}

function getSelectedTitle(row) {
  return row.name || row.description || row.action || row.event_type || 'Detail';
}

function getSelectedSubtitle(row) {
  return row.email || row.organization_name || row.project_name || row.actor_email || 'Detailed platform record';
}

function SelectedRowDetail({ row, formatMoney }) {
  const pairs = Object.entries(row || {});
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
      {pairs.map(([key, value]) => (
        <div key={key} className="rounded-2xl border border-slate-100 bg-slate-50 px-3 py-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
            {key.replace(/_/g, ' ')}
          </p>
          <div className="mt-1 break-words text-xs font-medium text-slate-700">
            {typeof value === 'number' && (key.includes('amount') || key.includes('budget'))
              ? formatMoney(value, row.currency || 'PKR')
              : String(value ?? '-')}
          </div>
        </div>
      ))}
    </div>
  );
}